import { useState } from "react";
import { makeStyles, mergeClasses, Dropdown, Option } from "@fluentui/react-components";
import { PaperPlaneRight, FunnelSimple } from "@phosphor-icons/react";
import type { Domain } from "@ethos/shared-types";

import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase200: tokens.primitives["font-Size-Base-200"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    fontWeightSemibold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    themeDarkAlt: tokens.palette["theme-DarkAlt"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralTertiaryAlt: tokens.semantic["neutral-TertiaryAlt"],
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    neutralLighter: tokens.semantic["neutral-Lighter"],

    // Component tokens
    inputBackground: tokens.components.inputBackground,
    inputBackgroundHover: tokens.components.inputBackgroundHover,
    inputBackgroundPressed: tokens.components.inputBackgroundPressed,
    inputStroke: tokens.components.inputStroke,
    inputStrokeHover: tokens.components.inputStrokeHover,
    inputStrokePressed: tokens.components.inputStrokePressed,
    inputForegroundHover: tokens.components.inputForegroundHover,

    // Button tokens
    btnSolidPrimaryBg: tokens.buttons.buttonSolidPrimaryBackground,
    btnSolidPrimaryFg: tokens.buttons.buttonSolidPrimaryForeground,
    btnSolidPrimaryBgHover: tokens.buttons.buttonSolidPrimaryBackgroundHover,
    btnSolidPrimaryFgDisabled: tokens.buttons.buttonSolidPrimaryForegroundDisabled,

    // Radius
    radiusMed: tokens.primitives["stroke-Radius-Medium"],
    radiusCircular: tokens.primitives["stroke-Radius-Circular"],

    // Shadows
    shadowRestingMed: tokens.shadows["shadow-Resting-Medium"],

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus,
    strokeWidthThin: tokens.primitives["stroke-Width-Thin"]
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalS,
        width: "100%",
        maxWidth: "900px",
        margin: "0 auto"
    },

    // Base input container - empty state
    inputContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: t.spacingHorizontalS,
        backgroundColor: t.inputBackground,
        border: `${t.strokeWidthThin} solid ${t.inputStroke}`,
        borderRadius: t.radiusMed,
        padding: `${t.spacingVerticalS} ${t.spacingHorizontalM}`,
        boxShadow: t.shadowRestingMed,
        transitionProperty: "border-color, background-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.inputBackgroundHover,
            border: `1px solid ${t.inputStrokeHover}`
        }
    },

    // Container when it has content
    inputContainerHasContent: {
        backgroundColor: t.inputBackgroundPressed,
        border: `1px solid ${t.inputStrokeHover}`,

        "&:hover": {
            backgroundColor: t.inputBackgroundPressed
        }
    },

    // Container when focused
    inputContainerFocused: {
        backgroundColor: t.inputBackgroundPressed,
        border: `1px solid ${t.inputStrokePressed}`,

        "&:hover": {
            backgroundColor: t.inputBackgroundPressed,
            border: `1px solid ${t.inputStrokePressed}`
        }
    },

    // Base icon button (shared)
    iconButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        minWidth: "24px",
        minHeight: "24px",
        padding: t.spacingVerticalXs,
        backgroundColor: "transparent",
        border: "none",
        borderRadius: t.radiusCircular,
        cursor: "pointer",
        color: t.neutralTertiaryAlt,
        flexShrink: 0,
        boxSizing: "border-box",
        transitionProperty: "color, background-color, box-shadow",
        transitionDuration: "0.15s",

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        },

        "&:disabled": {
            cursor: "not-allowed",
            opacity: 0.5
        }
    },

    // Clear button when it's enabled (can clear a chat)
    clearButtonEnabled: {
        backgroundColor: t.neutralLighterAlt,
        boxShadow: `inset 0 0 0 1px ${t.neutralLighterAlt}`,
        color: t.btnSolidPrimaryFgDisabled,

        "&:hover:not(:disabled)": {
            backgroundColor: t.btnSolidPrimaryBg,
            border: `4px solid ${t.btnSolidPrimaryBg}`,
            boxShadow: "none",
            boxSizing: "border-box",
            color: t.neutralLighterAlt
        }
    },

    // Clear button when container has content
    clearButtonHasContent: {
        backgroundColor: t.neutralLighterAlt,
        border: `1px solid ${t.neutralLighter}`,
        color: t.btnSolidPrimaryFgDisabled,

        "&:hover:not(:disabled)": {
            backgroundColor: t.btnSolidPrimaryBg,
            border: `1px solid ${t.btnSolidPrimaryBg}`,
            boxShadow: `inset 0 0 0 4px ${t.neutralWhite}`,
            color: t.btnSolidPrimaryFgDisabled
        }
    },

    // Clear button when container is focused
    clearButtonFocused: {
        backgroundColor: t.neutralLighterAlt,
        border: `1px solid ${t.neutralLighter}`,
        color: t.btnSolidPrimaryFgDisabled,

        "&:hover:not(:disabled)": {
            backgroundColor: t.btnSolidPrimaryBg,
            border: `1px solid ${t.btnSolidPrimaryBg}`,
            boxShadow: `inset 0 0 0 4px ${t.neutralWhite}`,
            color: t.btnSolidPrimaryFgDisabled
        }
    },

    // Domain selector pill — inline inside the input bar
    domainPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        flexShrink: 0,
        backgroundColor: t.neutralLighterAlt,
        border: `1px solid ${t.neutralLighter}`,
        borderRadius: t.radiusCircular,
        padding: `2px 4px 2px 8px`,
        cursor: "pointer",
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.neutralLighter,
            borderTopColor: t.inputStrokeHover,
            borderRightColor: t.inputStrokeHover,
            borderBottomColor: t.inputStrokeHover,
            borderLeftColor: t.inputStrokeHover,
        },

        "&:focus-within": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "1px",
        },
    },

    domainPillIcon: {
        display: "flex",
        alignItems: "center",
        color: t.neutralSecondary,
        flexShrink: 0,
    },

    domainDropdown: {
        minWidth: "unset",
        border: "none",
        backgroundColor: "transparent",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase200,
        fontWeight: t.fontWeightSemibold as unknown as number,
        color: t.neutralPrimary,
        cursor: "pointer",
        padding: "0 4px",

        "& button": {
            minWidth: "unset",
            border: "none",
            backgroundColor: "transparent",
            padding: "0 4px",
            fontSize: t.fontSizeBase200,
            fontWeight: t.fontWeightSemibold as unknown as number,
        },
    },

    // Vertical separator between pill and textarea
    separator: {
        width: "1px",
        alignSelf: "stretch",
        backgroundColor: t.neutralLighter,
        marginTop: "4px",
        marginBottom: "4px",
        flexShrink: 0,
    },

    textarea: {
        flex: 1,
        border: "none",
        outline: "none",
        resize: "none",
        backgroundColor: "transparent",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.neutralPrimary,
        padding: `${t.spacingVerticalS} 0`,
        minHeight: "24px",
        maxHeight: "120px",

        "&::placeholder": {
            color: t.neutralTertiaryAlt,
            transitionProperty: "color",
            transitionDuration: "0.15s"
        },

        "&:hover::placeholder": {
            color: t.inputForegroundHover
        }
    },

    // Send button base (disabled/empty state)
    sendButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        minWidth: "24px",
        minHeight: "24px",
        padding: t.spacingVerticalXs,
        background: "none",
        border: "none",
        borderRadius: t.radiusCircular,
        cursor: "pointer",
        color: t.neutralTertiaryAlt,
        flexShrink: 0,
        transitionProperty: "color, background-color",
        transitionDuration: "0.15s",

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        },

        "&:disabled": {
            cursor: "not-allowed"
        }
    },

    // Send button when active (has content)
    sendButtonActive: {
        backgroundColor: t.btnSolidPrimaryBg,
        color: t.btnSolidPrimaryFg,

        "&:hover:not(:disabled)": {
            backgroundColor: t.btnSolidPrimaryBgHover
        }
    },

    disclaimer: {
        textAlign: "center",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        color: t.neutralSecondary,
        margin: 0
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
    onSend: (question: string) => void;
    onClearChat: () => void;
    clearChatDisabled: boolean;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    disclaimerText?: string;
    /** Domain selector — all optional; omit to hide */
    domains?: Domain[];
    selectedDomainSlug?: string;
    onDomainChange?: (slug: string | undefined) => void;
    showDomainSelector?: boolean;
}

export const QuestionInput = ({
    onSend,
    onClearChat,
    clearChatDisabled,
    disabled,
    placeholder = "Type your question...",
    clearOnSend,
    disclaimerText = "Do not input sensitive information",
    domains,
    selectedDomainSlug,
    onDomainChange,
    showDomainSelector,
}: Props) => {
    const styles = useStyles();
    const [question, setQuestion] = useState<string>("");
    const [isFocused, setIsFocused] = useState(false);

    const hasContent = question.trim().length > 0;

    const sendQuestion = () => {
        if (disabled || !hasContent) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = ev.target.value;
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    const sendQuestionDisabled = disabled || !hasContent;

    // Determine container classes
    const containerClasses = mergeClasses(
        styles.inputContainer,
        hasContent && !isFocused && styles.inputContainerHasContent,
        isFocused && styles.inputContainerFocused
    );

    // Determine clear button classes
    const clearButtonClasses = mergeClasses(styles.iconButton, !clearChatDisabled && styles.clearButtonEnabled);

    // Determine send button classes
    const sendButtonClasses = mergeClasses(styles.sendButton, hasContent && styles.sendButtonActive);

    const hasDomainSelector = showDomainSelector && domains && domains.length > 1 && onDomainChange;

    return (
        <div className={styles.wrapper} id="chatInputContainer" role="search" aria-label="Chat message input">
            <div className={containerClasses}>
                <button
                    id="clearChatBtn"
                    className={clearButtonClasses}
                    onClick={onClearChat}
                    disabled={clearChatDisabled}
                    aria-label="Clear chat and start new chat"
                    title="Clear chat and start new chat"
                >
                    <img src={brand.clearChatIcon} alt="" width={34} height={34} aria-hidden="true" />
                </button>

                {hasDomainSelector && (
                    <>
                        <div className={styles.domainPill}>
                            <span className={styles.domainPillIcon}>
                                <FunnelSimple size={14} weight="bold" aria-hidden="true" />
                            </span>
                            <Dropdown
                                className={styles.domainDropdown}
                                appearance="underline"
                                size="small"
                                value={selectedDomainSlug ? domains.find((d) => d.slug === selectedDomainSlug)?.name ?? `Select a ${brand.domainNoun.singular}` : `Select a ${brand.domainNoun.singular}`}
                                selectedOptions={selectedDomainSlug ? [selectedDomainSlug] : []}
                                onOptionSelect={(_e, data) => onDomainChange(data.optionValue === "" ? undefined : data.optionValue)}
                                aria-label={`Filter search by ${brand.domainNoun.singular}`}
                            >
                                {/* Clear option — only shown when a base is currently
                                    selected. Selecting it routes through
                                    onOptionSelect's empty-value branch which calls
                                    onDomainChange(undefined), clearing localStorage
                                    and putting the dropdown back to "Select a base".
                                    The next chat turn then re-runs the base
                                    pre-flight ("Which base are you asking about?"). */}
                                {selectedDomainSlug && (
                                    <Option key="__clear" value="" text="Clear selection">
                                        Clear selection
                                    </Option>
                                )}
                                {domains.map((d) => (
                                    <Option key={d.id} value={d.slug}>
                                        {d.name}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className={styles.separator} role="separator" aria-hidden="true" />
                    </>
                )}

                <textarea
                    id="questionInput"
                    className={styles.textarea}
                    placeholder={placeholder}
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={disabled}
                    rows={1}
                    aria-label="Type your question"
                    aria-describedby="inputDisclaimer"
                />

                <button
                    className={sendButtonClasses}
                    onClick={sendQuestion}
                    disabled={sendQuestionDisabled}
                    aria-label={sendQuestionDisabled ? "Send question (enter text first)" : "Send question"}
                >
                    <PaperPlaneRight size={16} aria-hidden="true" />
                </button>
            </div>

            {disclaimerText && <p id="inputDisclaimer" className={styles.disclaimer}>{disclaimerText}</p>}
        </div>
    );
};
